import React, { useRef, ComponentPropsWithoutRef, ReactNode } from "react";
import NextLink from "next/link";
import { useAppRouter } from "lib/useAppRouter";
import { Route } from "lib/routes";
import useRouteAccess from "lib/useRouteAccess";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import links from "lib/links";
import { useCmdkStore } from "components/common/cmdk/cmdkStore";
import { useLaunchpadStore } from "components/launchpad/launchpadStore";
import { Text } from "@puzzle/ve";
import { Stack } from "@puzzle/ve";
import { ChevronDown, Crown, Gift, Search2, ExternalInline } from "@puzzle/icons";
import { S, vars } from "@puzzle/theme";
import {
  collapseChevronStyle,
  triggerRecipe,
  contentStyle,
  subItemLinkRecipe,
  cmkdKItemRecipe,
  rootItemLinkRecipe,
  itemRecipe,
  subItemRecipe,
  pendingActionIconStyle,
} from "./navItems.css";
import { DATA_TEST_IDS } from "./constants";
import { reportError } from "lib/errors/errors";
import { constructHref } from "@puzzle/utils";
import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";
import { useNavSidebarState } from "domains/app/components/MinimizeSidebarButton/MinimizeSidebarButton";
import { SidebarHoverCard } from "./SidebarHoverCard";
import Analytics from "lib/analytics/analytics";

// Helper hook to encapsulate common navigation item logic
const useNavItemBehavior = (
  route: Route,
  originalOnClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
) => {
  const { hasAccess } = useRouteAccess();
  const { company, companies } = useActiveCompany();
  const { isLaunchpadOverlayVisible, hideLaunchpadOverlay } = useLaunchpadStore();

  const { href: navHref, error: hrefError } = constructHref(
    route,
    companies?.length,
    company?.id ? { companyId: company.id } : undefined
  );

  if (hrefError) {
    reportError(
      `NavItems.tsx error calling constructHref function: ${hrefError.message}`,
      hrefError.payload
    );
  }

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    Analytics.sidebarLinkClicked({
      sidebarLocation: "baseSidebar",
      link: route,
    });

    if (isLaunchpadOverlayVisible) {
      hideLaunchpadOverlay();
    }
    originalOnClick?.(e);
  };

  const isRouteAccessible = hasAccess(route);

  return { navHref, isRouteAccessible, handleOnClick };
};

type NavSectionProps = {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  routes: Route[];
  sectionGap?: boolean;
  open: boolean;
  onClick: () => void;
} & ComponentPropsWithoutRef<"button">;

export const NavSection = ({
  title,
  icon,
  children,
  routes,
  sectionGap = false,
  open,
  onClick,
  ...buttonProps
}: NavSectionProps) => {
  const { hasAccess } = useRouteAccess();
  const contentRef = useRef<HTMLUListElement>(null);
  const { isNavMinimized } = useNavSidebarState();
  const isPartialMinimizedEnabled = isPosthogFeatureFlagEnabled(
    FeatureFlag.SidebarPartialMinimized
  );
  if (!routes.some(hasAccess)) {
    return null;
  }

  const sectionButton = (
    <button
      type="button"
      aria-label={`${title} section`}
      aria-haspopup="menu"
      className={triggerRecipe({ minimized: isNavMinimized })}
      onClick={onClick}
      {...buttonProps}
    >
      {icon}
      {!isNavMinimized && (
        <>
          <Text variant="bodyS" weight="bold">
            {title}
          </Text>
          <ChevronDown className={collapseChevronStyle} data-open={open ? "open" : undefined} />
        </>
      )}
    </button>
  );

  if (isPartialMinimizedEnabled && isNavMinimized) {
    return (
      <li style={{ marginTop: sectionGap ? S.$2 : "initial" }}>
        <SidebarHoverCard title={title} trigger={sectionButton}>
          {children}
        </SidebarHoverCard>
      </li>
    );
  }

  return (
    <li style={{ marginTop: sectionGap ? S.$2 : "initial" }}>
      {sectionButton}
      {!isNavMinimized && (
        <ul
          ref={contentRef}
          className={contentStyle}
          style={{
            height: open ? contentRef.current?.scrollHeight : 0,
          }}
        >
          {children}
        </ul>
      )}
    </li>
  );
};

type RootNavItemProps = {
  title: string;
  icon: ReactNode;
  route: Route;
  suffix?: ReactNode;
  sectionGap?: boolean;
} & ComponentPropsWithoutRef<"a">;

export const RootNavItem = ({
  title,
  icon,
  route,
  suffix,
  sectionGap = false,
  ...anchorProps
}: RootNavItemProps) => {
  const { getCurrentRoute } = useAppRouter();

  const { navHref, isRouteAccessible, handleOnClick } = useNavItemBehavior(
    route,
    anchorProps.onClick
  );
  const { isNavMinimized } = useNavSidebarState();
  const isPartialMinimizedEnabled = isPosthogFeatureFlagEnabled(
    FeatureFlag.SidebarPartialMinimized
  );

  const isActive = (() => {
    const currentRoute = getCurrentRoute();

    // For Dashboard, we want exact match
    if (title === "Dashboard") {
      return currentRoute === route;
    }

    // For other routes, check if the current route starts with the target route
    // This handles cases like /inbox/tasks where we want /inbox to be active
    return currentRoute.startsWith(route);
  })();

  if (!isRouteAccessible) {
    return null;
  }

  const rootNavTrigger = (
    <NextLink
      className={rootItemLinkRecipe({ minimized: isNavMinimized })}
      href={navHref}
      data-active={isActive ? "active" : undefined}
      {...anchorProps}
      onClick={handleOnClick}
    >
      <Stack direction="horizontal" css={{ gap: S.$1h, alignItems: "center" }}>
        {icon}
        {!isNavMinimized && (
          <>
            <Text variant="bodyS" weight="bold">
              {title}
            </Text>
            {suffix}
          </>
        )}
      </Stack>
    </NextLink>
  );

  if (isPartialMinimizedEnabled && isNavMinimized) {
    return (
      <li
        className={itemRecipe({ minimized: isNavMinimized })}
        style={{ marginTop: sectionGap ? S.$2 : "initial" }}
      >
        <SidebarHoverCard title={title} trigger={rootNavTrigger} />
      </li>
    );
  }

  return (
    <li
      className={itemRecipe({ minimized: isNavMinimized })}
      style={{ marginTop: sectionGap ? S.$2 : "initial" }}
    >
      {rootNavTrigger}
    </li>
  );
};

type SubNavItemProps = {
  title: string;
  route: Route;
  featureGated?: boolean;
  checkEntireRoute?: boolean;
  suffix?: ReactNode;
  tabs?: Route[];
} & ComponentPropsWithoutRef<"a">;

export const SubNavItem = ({
  title,
  route,
  featureGated = false,
  checkEntireRoute = false,
  suffix,
  tabs,
  ...anchorProps
}: SubNavItemProps) => {
  const { getCurrentRoute, router } = useAppRouter();
  const { navHref, isRouteAccessible, handleOnClick } = useNavItemBehavior(
    route,
    anchorProps.onClick
  );

  // some pages have multiple tabs, so we need to check if any of the routes are active
  const isTabActive = tabs?.some((r) => getCurrentRoute() === r);
  const { isNavMinimized } = useNavSidebarState();
  const isActive = (() => {
    const currentRoute = getCurrentRoute();

    // If we need to check the entire route (including query parameters)
    if (checkEntireRoute) {
      // Use URL API to extract the pathname without query parameters
      const url = new URL(route, window.location.origin);
      const basePath = url.pathname;
      return router.asPath.includes(basePath);
    }

    // For tab-based navigation, check if any tab is active
    if (isTabActive) {
      return true;
    }

    // For regular routes, check if the current route matches the base path
    const url = new URL(route, window.location.origin);
    const basePath = url.pathname;
    return currentRoute === basePath;
  })();

  if (!isRouteAccessible) {
    return null;
  }

  return (
    <li className={subItemRecipe({ minimized: isNavMinimized })}>
      <NextLink
        className={subItemLinkRecipe({ minimized: isNavMinimized })}
        href={navHref}
        data-active={isActive ? "active" : undefined}
        {...anchorProps}
        onClick={handleOnClick}
      >
        <Stack direction="horizontal" css={{ gap: S.$1h, alignItems: "center" }}>
          <Text variant="bodyS" weight="bold">
            {title}
          </Text>
          {suffix}
          {featureGated && (
            <Crown color={vars.colors["text-neutral-mostSubdue"]} style={{ marginLeft: "auto" }} />
          )}
        </Stack>
      </NextLink>
    </li>
  );
};

export const CmdKItem = () => {
  const { setIsCmdkOpen, isCmdkOpen } = useCmdkStore();
  const { isNavMinimized } = useNavSidebarState();
  return (
    <li
      data-testid={DATA_TEST_IDS.CMD_ITEM}
      className={cmkdKItemRecipe({ minimized: isNavMinimized })}
      onClick={() => setIsCmdkOpen(true)}
      data-active={isCmdkOpen ? "active" : undefined}
    >
      <Search2 size={20} />
      {!isNavMinimized && (
        <>
          <Text variant="bodyS">Shortcuts</Text>
          <span style={{ marginLeft: "auto" }}>
            <Text variant="bodyXS">⌘K</Text>
          </span>
        </>
      )}
    </li>
  );
};

const PERKS_AND_REWARDS_TITLE = "Perks & rewards";
export const RewardsItem = () => {
  const { isNavMinimized } = useNavSidebarState();
  const isPartialMinimizedEnabled = isPosthogFeatureFlagEnabled(
    FeatureFlag.SidebarPartialMinimized
  );
  const trigger = (
    <NextLink
      className={rootItemLinkRecipe({ minimized: isNavMinimized })}
      href={links.partnerRewards}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Stack direction="horizontal" css={{ gap: S.$1h, alignItems: "center" }}>
        <Gift size={20} />
        {!isNavMinimized && (
          <>
            <Text variant="bodyS" weight="bold">
              {PERKS_AND_REWARDS_TITLE}
            </Text>
            <ExternalInline />
          </>
        )}
      </Stack>
    </NextLink>
  );

  if (isPartialMinimizedEnabled && isNavMinimized) {
    return (
      <li className={itemRecipe({ minimized: isNavMinimized })} style={{ marginTop: "initial" }}>
        <SidebarHoverCard title={PERKS_AND_REWARDS_TITLE} trigger={trigger} />
      </li>
    );
  }

  return (
    <li
      data-testid={DATA_TEST_IDS.PARTNER_REWARDS}
      className={itemRecipe({ minimized: isNavMinimized })}
      style={{ marginTop: "initial" }}
    >
      {trigger}
    </li>
  );
};

export const PendingActionIcon = () => {
  return <div className={pendingActionIconStyle} />;
};
